import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import type { OnboardingScreen } from './next-unanswered-step'
import { groupIntoSections, type OnboardingProgressProps } from './onboarding-progress'

const STEP_LABEL_KEY: Record<OnboardingScreen, string> = {
	language: 'onboarding.sections.language',
	company: 'onboarding.sections.company',
	organisation_headcount: 'onboarding.steps.organisationHeadcount',
	manages_team: 'onboarding.steps.managesTeam',
	contacts_candidates: 'onboarding.steps.contactsCandidates',
	recruiting_volume: 'onboarding.steps.recruitingVolume',
	phone_number: 'onboarding.steps.phoneNumber',
	billing: 'onboarding.sections.billing',
	free_trial: 'onboarding.sections.freeTrial',
}

export function OnboardingSteps(
	{ step, steps, furthestStep, isReachable, onSelect, disabled }: OnboardingProgressProps,
) {
	const { t } = useTranslation()
	const furthestIndex = furthestStep === 'complete' ? steps.length : steps.indexOf(furthestStep)
	const isAnswered = (sectionStep: OnboardingScreen) => steps.indexOf(sectionStep) < furthestIndex
	const sections = groupIntoSections(steps)

	return (
		<nav aria-label={t('onboarding.progress.label')}>
			<ol className="flex flex-col">
				{sections.map(({ section, steps: sectionSteps }, index) => {
					const isCurrent = sectionSteps.includes(step)
					const isDone = !isCurrent && sectionSteps.every(isAnswered)
					const target = isCurrent
						? undefined
						: sectionSteps.find((sectionStep) => sectionStep === furthestStep)
							?? sectionSteps.find(isReachable)
					const label = t(`onboarding.sections.${section}`)

					return (
						<li key={section} className="relative pb-7 last:pb-0">
							{index < sections.length - 1 && (
								<span
									aria-hidden
									className={cn(
										'absolute left-3.5 top-9 bottom-2 w-px -translate-x-1/2',
										isDone ? 'bg-brand-300' : 'bg-brand-100',
									)}
								/>
							)}
							<button
								type="button"
								onClick={() => target && onSelect(target)}
								disabled={disabled || !target}
								aria-current={isCurrent ? 'step' : undefined}
								className="group flex items-center gap-3 text-left disabled:cursor-default"
							>
								<span
									aria-hidden
									className={cn(
										'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
										isCurrent && 'bg-brand-600 text-white ring-4 ring-brand-100',
										isDone && 'bg-brand-100 text-brand-700',
										!isCurrent && !isDone
											&& 'border border-brand-200 bg-card text-muted-foreground',
									)}
								>
									{isDone ? <Check aria-hidden className="size-4" /> : index + 1}
								</span>
								<span
									className={cn(
										'text-sm',
										isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground',
										target && !disabled && 'group-hover:text-foreground',
									)}
								>
									{label}
								</span>
							</button>
							<AnimatePresence initial={false}>
								{isCurrent && sectionSteps.length > 1 && (
									<motion.ol
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: 'auto', opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
										className="ml-10 flex flex-col gap-2.5 overflow-hidden pt-3"
									>
										{sectionSteps.map((sectionStep) => {
											const isCurrentStep = sectionStep === step
											const canSelect = !isCurrentStep && isReachable(sectionStep)

											return (
												<li key={sectionStep}>
													<button
														type="button"
														onClick={() => onSelect(sectionStep)}
														disabled={disabled || !canSelect}
														aria-current={isCurrentStep ? 'true' : undefined}
														className={cn(
															'flex items-center gap-2.5 text-left text-sm disabled:cursor-default',
															isCurrentStep && 'font-medium text-brand-700',
															!isCurrentStep && isAnswered(sectionStep)
																&& 'text-muted-foreground',
															!isCurrentStep && !isAnswered(sectionStep)
																&& 'text-muted-foreground/60',
															canSelect && !disabled && 'hover:text-foreground',
														)}
													>
														<span
															aria-hidden
															className={cn(
																'size-1.5 shrink-0 rounded-full',
																isCurrentStep && 'bg-brand-600',
																!isCurrentStep && isAnswered(sectionStep)
																	&& 'bg-brand-300',
																!isCurrentStep && !isAnswered(sectionStep)
																	&& 'bg-brand-100',
															)}
														/>
														{t(STEP_LABEL_KEY[sectionStep])}
													</button>
												</li>
											)
										})}
									</motion.ol>
								)}
							</AnimatePresence>
						</li>
					)
				})}
			</ol>
		</nav>
	)
}
